import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Slider } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { getSubRoot } from "@/utils/utils";
import { fsIconImgSx, iconButtonSx, iconButtonBig, glassPanelSx } from "@/constants/styles";
import { glassIconHoverSx } from "@/styles/iconHover";
// TODO: Assicurati che i percorsi delle icone siano corretti nel tuo progetto
import playIcon from "../../icon/play.svg";
import pauseIcon from "../../icon/pause.svg";
import editIcon from "../../icon/edit2.svg";
import minimizeIcon from "../../icon/minimize.svg";
import Rating from '@mui/material/Rating';
import Typography from '@mui/material/Typography';
import SmoothJumpVideoPlayer from "./SmoothJumpVideoPlayer/SmoothJumpVideoPlayer";
import {getCardTitle} from "@/pages/custom-components/clipCard";
import {useIntl} from "react-intl";
import {trackAction} from "@/queries/tracking";
import {TRACKING_CONTENT_TYPE, UI_TRACKING_ACTION} from "@/types/trackingTypes";

const glassButtonSx = glassIconHoverSx();

const FullScreenControlPanel = ({
    jumpPlayerRef,
    card,
    state,
    thumbnailMap = {},
    editFlag, 
    onEditClick,
    formatPlayTime,
    volume,
    handleSliderChange,
    setMuted,
    muted,
    clipType
}) => {
    // --- Refs e Stati ---
    const hideTimeoutRef = useRef(null);
    const [controlsVisible, setControlsVisible] = useState(true);

    const intl = useIntl();

    // --- Pulizia del timeout al montaggio/smontaggio ---
    useEffect(() => {
        return () => {
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        };
    }, []);

    // --- Gestori Eventi ---
    const handleMouseMove = (isPlaying) => {
        setControlsVisible(true);
        
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
        }

        if (isPlaying) {
            hideTimeoutRef.current = setTimeout(() => {
                setControlsVisible(false);
            }, 3000); // Nasconde dopo 3 secondi di inattività
        }
    };

    const handleMouseLeave = () => {
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        setControlsVisible(false);
    };

    // Ricava il poster in modo sicuro controllando l'esistenza di getSubRoot
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const posterSrc = card?.thumbnail || 
                      (getSubRoot ? thumbnailMap[getSubRoot(currentPath)] : undefined) || 
                      undefined;

    return (
        <Box sx={state.isPlaying ? {
                // STILE IN PLAY (Quello da te richiesto)
                position: 'absolute', 
                pointerEvents: 'none', 
                bottom: 0, 
                left: 0,
                right: 0, 
                height: '80px', 
                gap: 2, 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center', 
                px: 3, 
                pb: 1, 
                background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)', 
                zIndex: 10
            } : {
                // STILE IN PAUSA (Centrato verticalmente, alto 40vh)
                position: 'absolute', 
                pointerEvents: 'none',
                top: '25vh',
                height: '50vh',
                width:'100%',
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center', 
                background: 'linear-gradient(to top, transparent 0%, rgba(0,0,0,0.4) 15%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.4) 85%, transparent 100%)',
                px: 3,
                zIndex: 10
            }}
        >
            {/* CARD INFO PANEL*/}
            {!state.isPlaying && <Box sx={glassPanelSx} onClick={(e) => e.stopPropagation()}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#aaa', letterSpacing: 1.5, fontWeight: 600 }}>
                        {card?.rubric}
                    </Typography>
                    <Box sx={{ display: 'flex' }}>
                        <Rating precision={0.1} name="size-small" value={card.rating} size="small" readOnly />
                    </Box>
                </Box>
                
                <Typography variant="h3" sx={{ fontWeight: 600, mt: 0.5, letterSpacing: 0.5, maxWidth: 500 }}>
                    {(card?.event?.home_team !== null && card?.event?.home_team !== undefined) ? `${card?.event?.home_team} - ${card?.event?.away_team}` : `${intl.formatMessage({ id: getCardTitle(card) || '', defaultMessage: getCardTitle(card) || '' })}`}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 1 }}>
                    <Box>
                        <Typography variant="h1" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
                            {(card?.event?.home_team !== null && card?.event?.home_team !== undefined) ? intl.formatMessage({ id: getCardTitle(card) || '', defaultMessage: getCardTitle(card) || '' }) : card?.eyebrow}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: '#ccc', textTransform: 'uppercase', letterSpacing: 1 }}>
                            {card?.lead}
                        </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {formatPlayTime()}
                    </Typography>
                </Box>
            </Box>}

            {/* CONTROLLO VOLUME con slider */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mr: 'auto',
                    pointerEvents: 'auto',
                    transform: 'translateY(2px)',
                    '&:hover .volume-slider-box': {
                        width: '80px',
                        opacity: 1,
                        visibility: 'visible',
                        ml: 1,
                    }
                }}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
            >
                {/* BOTTONE ICONA */}
                {state.isPlaying && <React.Fragment>
                    <IconButton
                        sx={{ ...(state.isPlaying ? iconButtonSx : iconButtonBig), p: 1 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setMuted(prev => !prev);
                        }}
                    >
                        {muted ? <VolumeOffIcon /> : <VolumeUpIcon  />}
                    </IconButton>

                    <Box
                        className="volume-slider-box"
                        sx={{
                            width: 0,
                            opacity: 0,
                            visibility: 'hidden',
                            transition: 'all 0.2s ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Slider
                            disabled={!!muted}
                            value={volume}
                            onChange={handleSliderChange}
                            min={0}
                            max={100}
                            size="small"
                            sx={{
                                color: 'white',
                                padding: '13px 0',
                                '& .MuiSlider-thumb': {
                                    width: 12,
                                    height: 12
                                }
                            }}
                        />
                    </Box>
                </React.Fragment>}
            </Box>

            <Box sx={{
                display: 'flex',
                gap: 2,
                position: 'absolute',
                left: state.isPlaying ? '50%' : '75%',
                transform: 'translateX(-50%)',
                pointerEvents: 'auto'
            }}>
                <IconButton sx={{...(state.isPlaying ? iconButtonSx : iconButtonBig), pointerEvents:'auto'}} onClick={(e) => { e.stopPropagation(); jumpPlayerRef.current?.toggleFullscreen(); }}>
                    <Box component="img" src={minimizeIcon} alt="exit fullscreen" sx={fsIconImgSx} />
                </IconButton>
                <IconButton sx={{...(state.isPlaying ? iconButtonSx : iconButtonBig), pointerEvents:'auto'}} onClick={(e) => {
                    e.stopPropagation();
                    if (state.isPlaying)
                        jumpPlayerRef.current?.pause();
                    else
                        jumpPlayerRef.current?.play();
                    trackAction(state.isPlaying ? UI_TRACKING_ACTION.CONTENT_PAUSE : UI_TRACKING_ACTION.CONTENT_PLAY, {
                        content_id: card.id,
                        content_type: clipType === "key_moments" ? TRACKING_CONTENT_TYPE.KEY_MOMENT : TRACKING_CONTENT_TYPE.CLIP,
                        name: card.title,
                        event_name: card.eyebrow,
                        competition_name:card.rubric
                    })
                }}>
                    <Box component="img" src={state.isPlaying ? pauseIcon : playIcon} alt={state.isPlaying ? "pause" : "play"} sx={fsIconImgSx} />
                </IconButton>
                {editFlag && (
                    <IconButton sx={{...(state.isPlaying ? iconButtonSx : iconButtonBig), pointerEvents:'auto'}} onClick={onEditClick}>
                        <Box component="img" src={editIcon} alt="edit" sx={fsIconImgSx} />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
};

export default FullScreenControlPanel;